import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Cpu, Loader2, Clock, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

export const AiJobsChip = () => {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [allowed, setAllowed] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/rfp/ai-jobs`, { withCredentials: true });
        setJobs(res.data?.jobs || []);
      } catch (err) {
        if (err?.response?.status === 403) { setAllowed(false); clearInterval(pollRef.current); }
      }
    };
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, []);

  const cancelJob = async (job) => {
    try {
      await axios.post(`${API_URL}/api/rfp/ai-jobs/${job.id}/cancel`, {}, { withCredentials: true });
      toast.success(`Cancelled "${job.label}" before it started`);
      setJobs(js => js.filter(j => j.id !== job.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel — it may have already started');
    }
  };

  if (!allowed || jobs.length === 0) return null;
  const running = jobs.filter(j => j.status === 'running').length;
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1 hover:bg-sky-100 transition-colors"
        data-testid="rfp-ai-jobs-chip"
        title="AI jobs processing right now — heavy jobs run one at a time so they never compete">
        <Loader2 className="w-3 h-3 animate-spin" />
        {jobs.length} AI job{jobs.length === 1 ? '' : 's'}{jobs.length > running ? ` (${jobs.length - running} queued)` : ''}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-50 w-80 bg-white border border-slate-200 rounded-md shadow-lg p-2 space-y-1"
          data-testid="rfp-ai-jobs-list">
          {jobs.map((j, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] px-1.5 py-1 rounded hover:bg-slate-50">
              {j.status === 'running'
                ? <Cpu className="w-3 h-3 text-emerald-600 shrink-0" />
                : <Clock className="w-3 h-3 text-amber-600 shrink-0" />}
              <span className="text-slate-700 truncate flex-1" title={j.label}>{j.label}</span>
              <Badge variant="outline" className={`text-[8px] shrink-0 ${j.status === 'running'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                {j.status === 'running' ? `running ${fmt(j.running_s)}` : `queued ${fmt(j.waiting_s)}`}
              </Badge>
              {j.cancellable && (
                <button onClick={() => cancelJob(j)} title="Cancel this queued job before it starts"
                  className="text-slate-300 hover:text-rose-600 shrink-0"
                  data-testid={`rfp-ai-job-cancel-${j.id}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
